import React from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Filter, Plus, Eye, EyeOff } from 'lucide-react';
import { format, addMonths, subMonths, addYears, subYears, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ViewMode } from '@shared/types';

interface PlanningToolbarProps {
    currentDate: Date;
    setCurrentDate: (date: Date) => void;
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    services: string[];
    selectedService: string;
    setSelectedService: (service: string) => void;
    showWeekends: boolean;
    setShowWeekends: (show: boolean) => void;
    onNewAssignment?: () => void;
    showBacklog?: boolean;
    setShowBacklog?: (show: boolean) => void;
}

export const PlanningToolbar: React.FC<PlanningToolbarProps> = ({
    currentDate,
    setCurrentDate,
    viewMode,
    setViewMode,
    services,
    selectedService,
    setSelectedService,
    showWeekends,
    setShowWeekends,
    onNewAssignment,
    showBacklog,
    setShowBacklog
}) => {

    const handlePrev = () => {
        if (viewMode === 'year') {
            setCurrentDate(subYears(currentDate, 1));
        } else if (viewMode === 'week') {
            setCurrentDate(subWeeks(currentDate, 1));
        } else {
            setCurrentDate(subMonths(currentDate, 1));
        }
    };

    const handleNext = () => {
        if (viewMode === 'year') {
            setCurrentDate(addYears(currentDate, 1));
        } else if (viewMode === 'week') {
            setCurrentDate(addWeeks(currentDate, 1));
        } else {
            setCurrentDate(addMonths(currentDate, 1));
        }
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    let dateLabel = '';
    if (viewMode === 'year') {
        dateLabel = format(currentDate, 'yyyy', { locale: fr });
    } else if (viewMode === 'week') {
        // Example: "Semaine 2 - Janvier 2026" or "29 Déc - 04 Jan 2026"
        // Let's go with "29 Déc. - 04 Janv." for clarity
        // Calculate end of week for display
        // Assuming currentDate is start of week or inside it, but let's just show week number + Month
        // Simple: "Janvier 2026 - Semaine 05"
        dateLabel = `${format(currentDate, 'MMMM yyyy', { locale: fr })} - Sem. ${format(currentDate, 'w', { locale: fr })}`;
    } else {
        dateLabel = format(currentDate, 'MMMM yyyy', { locale: fr });
    }

    return (
        <div className="flex flex-col gap-4 mb-4 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 shadow-sm transition-colors">
            {/* Top Row: Title & Main Controls */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                {/* Date Navigation */}
                <div className="flex items-center gap-2 bg-[var(--input-bg)] rounded-lg p-1 border border-[var(--input-border)]">
                    <button
                        onClick={handlePrev}
                        className="p-1.5 hover:bg-[var(--glass-border)] rounded-md text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                        title="Précédent"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    <div className="flex items-center gap-2 px-2 min-w-[140px] justify-center font-semibold text-[var(--text-main)] capitalize">
                        <CalendarIcon size={18} className="text-primary opacity-80" />
                        <span>{dateLabel}</span>
                    </div>

                    <button
                        onClick={handleNext}
                        className="p-1.5 hover:bg-[var(--glass-border)] rounded-md text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                        title="Suivant"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                {/* Right Side Actions */}
                <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">

                    <button
                        onClick={handleToday}
                        className="px-3 py-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-main)] border border-[var(--glass-border)] rounded-lg hover:bg-[var(--glass-border)] transition-colors whitespace-nowrap"
                    >
                        Aujourd'hui
                    </button>

                    <div className="h-8 w-px bg-[var(--glass-border)] hidden md:block"></div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-[var(--input-bg)] rounded-lg p-1 border border-[var(--input-border)]">
                        <button
                            onClick={() => setViewMode('week')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'week'
                                ? 'bg-white shadow-sm text-primary dark:bg-primary dark:text-white'
                                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
                                }`}
                        >
                            Semaine
                        </button>
                        <button
                            onClick={() => setViewMode('month')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'month'
                                ? 'bg-white shadow-sm text-primary dark:bg-primary dark:text-white'
                                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
                                }`}
                        >
                            Mois
                        </button>
                        <button
                            onClick={() => setViewMode('year')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'year'
                                ? 'bg-white shadow-sm text-primary dark:bg-primary dark:text-white'
                                : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
                                }`}
                        >
                            Année
                        </button>
                    </div>

                    <div className="h-8 w-px bg-[var(--glass-border)] hidden md:block"></div>

                    {onNewAssignment && (
                        <button
                            onClick={onNewAssignment}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all font-medium whitespace-nowrap shadow-lg shadow-primary/20"
                        >
                            <Plus size={18} />
                            <span>Nouvelle Intervention</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Bottom Row: Filters (Optional, collapsible could be better but let's keep it visible for utility) */}
            <div className="flex flex-wrap items-center gap-3 text-sm pt-3 border-t border-[var(--glass-border)]">

                {setShowBacklog && (
                    <button
                        onClick={() => setShowBacklog(!showBacklog)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-lg border transition-colors ${showBacklog
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'text-[var(--text-muted)] border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                            }`}
                    >
                        {showBacklog ? <Eye size={14} /> : <EyeOff size={14} />}
                        <span className="font-medium">À Planifier</span>
                    </button>
                )}

                <div className="h-4 w-px bg-[var(--glass-border)] mx-1"></div>

                <div className="flex items-center gap-2 text-[var(--text-muted)]">
                    <Filter size={16} />
                    <span className="font-medium">Filtres :</span>
                </div>

                {/* Service Filter */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar max-w-full">
                    <button
                        onClick={() => setSelectedService('Tous')}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${selectedService === 'Tous'
                            ? 'bg-primary/10 text-primary border-primary/20'
                            : 'bg-[var(--glass-border)]/50 text-[var(--text-muted)] border-transparent hover:border-[var(--glass-border)]'
                            }`}
                    >
                        Tous
                    </button>
                    {services.map(service => (
                        <button
                            key={service}
                            onClick={() => setSelectedService(service)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${selectedService === service
                                ? 'bg-primary/10 text-primary border-primary/20'
                                : 'bg-[var(--glass-border)]/50 text-[var(--text-muted)] border-transparent hover:border-[var(--glass-border)]'
                                }`}
                        >
                            {service}
                        </button>
                    ))}
                </div>

                <div className="flex-1"></div>

                {/* Options */}
                <button
                    onClick={() => setShowWeekends(!showWeekends)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${showWeekends
                        ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                        : 'text-[var(--text-muted)] border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                        }`}
                >
                    {showWeekends ? <Eye size={14} /> : <EyeOff size={14} />}
                    <span className="text-xs font-medium">Week-ends</span>
                </button>
            </div>
        </div >
    );
};
